import { Component, computed, inject, input, Input, OnChanges, OnInit, signal, SimpleChanges, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FilterInput } from '@it2go/data-grid';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { Router } from '@angular/router';
import { WorkGridItemObject } from '@it2go/types';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import {
    filesColumn,
    folderPersonColumn,
    timeFromColumn,
    timeToColumn,
    usersChipColumn,
} from '../../../../shared/components/kit/table/table.fields';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { GlobalStore } from '../../../../../store/global.global-store';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { UnrealizedVisitListStore } from './unrealized-visit-list.store';
import { workHideDelete } from '../../../../../components/medical-service/work/work-detail/utils/work.transform-utils';
import { successDeleted } from '../../../content.error';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { WorkPrintService } from '../../../../../components/medical-service/work/work-print.service';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-unrealized-visit-list',
    templateUrl: './unrealized-visit-list.component.html',
    styleUrl: './unrealized-visit-list.component.scss',
    providers: [UnrealizedVisitListStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableComponent,
        ButtonComponent,
        PrimeTemplate,
    ],
})
export class UnrealizedVisitListComponent extends SubscriptionComponent implements OnInit, OnChanges {
    protected readonly store = inject(UnrealizedVisitListStore);
    private readonly router = inject(Router);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly globalStore = inject(GlobalStore);
    protected readonly workPrint = inject(WorkPrintService);
    protected readonly extendedWorkExportsEnabled = featureOn.EXTENDED_WORK_EXPORTS;


    protected readonly folderStore = inject(FolderGlobalStore);

    @Input() set extraFilters(filters: FilterInput[][]) {
        this._extraFilters.set(filters);
    }

    @Input() folderId = 0;
    public readonly printDateFrom = input<Date | null>(null);
    public readonly printDateTo = input<Date | null>(null);

    protected readonly tableComponent = viewChild(TableComponent);

    protected baseFilters = computed<FilterInput[][]>(() => [
        [
            {
                value: [WorkTypeEnum.UNREALIZED_VISIT],
                column: 'workType',
                operator: 'EQ',
            },
        ],
        [
            {
                value: [String(this.globalStore.organizationStore.selectedId())],
                column: 'organizationId',
                operator: 'EQ',
            },
        ],
    ]);
    protected additionalFilters = computed<FilterInput[][]>(() => [
        ...this.baseFilters(),
        ...this._extraFilters(),
        ...this._folderFilter(),
    ]);

    protected columns: TableColumn[] = [];

    private _extraFilters: WritableSignal<FilterInput[][]> = signal([]);
    private _folderFilter: WritableSignal<FilterInput[][]> = signal([]);

    ngOnInit(): void {
        this.columns = [
            ...!(this.folderId) ? [folderPersonColumn] : [],
            {
                name: 'date',
                type: TableColumnType.Date,
                dataKey: 'dateFrom',
            },
            timeFromColumn,
            timeToColumn,
            {
                name: 'workData.uv.note',
                dataKey: 'workData.note',
                type: TableColumnType.String50,
                filterable: true,
            },
            {
                name: 'workData.uv.voucherNumber',
                dataKey: 'workData.voucherNumber',
                filterable: {
                    type: 'number',
                },
            },
            filesColumn,
            usersChipColumn,
        ];
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.folderId) {
            this._folderFilter.set([
                [
                    {
                        value: [this.folderId.toString()],
                        column: 'folderId',
                        operator: 'EQ',
                    },
                ],
            ]);
        }
    }

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(input: any): void {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteWork({ id: input.item.id });
                successDeleted();
                this.tableComponent()?.reload();
            },
            input,
        );
    }

    protected readonly workHideDelete = workHideDelete(this.store.globalGlobalStore);

    protected printRecords(): void {
        this.workPrint.openBulk({
            selection: this.tableComponent()?.selection(),
            supportedWorkTypes: [WorkTypeEnum.UNREALIZED_VISIT],
            dateFrom: this.printDateFrom(),
            dateTo: this.printDateTo(),
            folderIds: this.folderId ? [this.folderId] : [],
        });
    }

    protected printRecord(item: WorkGridItemObject): void {
        this.workPrint.openRow(item);
    }

}
